import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  GlobalOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
  TeamOutlined,
  TrophyOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  App as AntdApp,
  Badge,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUpload from '../components/ImageUpload';
import { LogoThumb } from '../components/LogoThumb';
import SponsorDelegates from '../components/SponsorDelegates';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * A szponzor közösségi mezői (#156).
 *
 * A kulcskészlet zárt, és a backend `SponsorSocialLinksDto`-jának a tükre: ami
 * nincs felsorolva, azt a szerver `forbidNonWhitelisted`-je 400-zal dobja el.
 * A hálózatok nevei tulajdonnevek — nem fordítjuk őket.
 */
const SOCIAL_NETWORKS = ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube'] as const;

type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

const SOCIAL_FIELDS: { key: SocialNetwork; label: string; placeholder: string }[] = [
  { key: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/...' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/...' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/company/...' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@...' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/@...' },
];

/**
 * A közösségi mezők küldhető alakja.
 *
 * Az űrlap üres mezőre `undefined`-ot ad, a szervező pedig könnyen hagy szóközt
 * a beillesztett cím elején — az utóbbi a backend `new URL()` ellenőrzésén 400-at
 * adna, holott a cím jó. Ezért itt vágunk és szűrünk.
 *
 * **Mindig objektumot küldünk, akkor is, ha üres.** Enélkül a mind az öt mező
 * kiürítése nem törölné a tárolt linkeket: a hiányzó kulcs a `PATCH`
 * szemantikájában „ne változtass"-t jelent. Az üres objektum ára annyi, hogy egy
 * szponzor sora `NULL` helyett `{}`-t kap, ha a szervező szerkeszti — ezt
 * egyetlen olvasó sem különbözteti meg.
 *
 * **A `trim()` NEM az űrlap miatt van, és ez mérve van.** A mezők
 * `<Input type="url">`-ök, a HTML `url` típus értékszanálása pedig maga vágja le
 * a körülvevő szóközt — mutációval igazolva: a `trim()` elhagyása mellett az
 * űrlapot rendereltető próba ZÖLD maradt. A vágás azért marad itt, mert a
 * mezőtípus egy későbbi átalakításkor megváltozhat, és a szerver oldalán egy
 * szóköz `new URL()`-en 400-at ad. A saját próbája ezért közvetlen, nem az
 * űrlapon át — lásd `Sponsors.socialLinks.test.tsx`.
 */
export function normalizeSocialLinks(raw: unknown): Record<string, string> {
  const source = (raw ?? {}) as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const key of SOCIAL_NETWORKS) {
    const value = typeof source[key] === 'string' ? source[key].trim() : '';
    if (value) out[key] = value;
  }
  return out;
}

interface Sponsor {
  id: string;
  name: string;
  logo?: string;
  website?: string;
  description?: string;
  /** A látogatói szponzor-oldal részletek-panelének hosszú szövege (#156). */
  longDescription?: string;
  socialLinks?: Partial<Record<SocialNetwork, string>>;
  level?: 'PLATINUM' | 'GOLD' | 'SILVER' | 'BRONZE' | 'PARTNER';
  sponsorLevelId?: string;
  EventSponsorLevel?: {
    id: string;
    name: string;
    level: number;
    color?: string;
  };
  contactName?: string | null;
  contactEmail?: string | null;
  bonusFreeTickets?: number;
  eventId: string;
  Event?: {
    id: string;
    title: string;
    Organization?: {
      name: string;
    };
  };
}

/**
 * A szervezői összesítő (#150) — a keret, a link állapota és a kapcsolók.
 *
 * SZÁNDÉKOSAN külön végpontból jön, nem a `GET /sponsors` listából: az
 * `@Public()`, és a #148 óta whitelistelt, tehát ezeket a mezőket nem adja ki.
 */
/**
 * A #163 HITELESÍTETT olvasásának sora (`GET /events/:id/sponsors/admin`,
 * `GET /sponsors/:id/admin`).
 *
 * Külön típus a `Sponsor`-tól, és ez szándékos. A `Sponsor` a tábla sora, ami a
 * `@Public() GET /sponsors`-ról jön — annak a whitelistje a kapcsolattartót
 * SOHA nem adja ki. A #162 pont abból lett, hogy a típus úgy tett, mintha
 * kiadná: a `setFieldsValue(record)` teljesnek LÁTSZOTT, és a két mező
 * csendben az előző szponzoré maradt. Két forrás, két típus.
 */
interface AdminSponsorRow {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  companyId: string | null;
  lastSubmittedAt: string | null;
  Company: {
    id: string;
    name: string;
    taxNumber: string | null;
    postalCode: string | null;
    city: string | null;
    streetAddress: string | null;
    contactPerson: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
  } | null;
}

interface SponsorSummaryRow {
  id: string;
  name: string;
  levelName: string | null;
  allowance: {
    free: number;
    paidExtra: number;
    remainingFree: number;
    remainingPaid: number;
    waived: boolean;
    overbooked: boolean;
  };
  occupied: { free: number; paid: number };
  awaitingPayment: number;
  settledPaid: number;
  link: {
    state: 'NOT_SENT' | 'SENT' | 'OPENED' | 'SUBMITTED' | 'REVOKED' | 'EXPIRED';
    sentAt: string | null;
    openedAt: string | null;
    expiresAt: string | null;
    lastSubmittedAt: string | null;
  };
}

interface SponsorSummary {
  config: { selfService: boolean; paidExtraTickets: boolean };
  totals: {
    sponsors: number;
    freeEntitled: number;
    freeFilled: number;
    paidSeats: number;
    paidSettled: number;
    waivedSeats: number;
  };
  sponsors: SponsorSummaryRow[];
}

interface Event {
  id: string;
  title: string;
  organizationId: string;
  isActive?: boolean;
  endDate?: string;
}

interface SponsorLevel {
  id: string;
  name: string;
  level: number;
  color?: string;
  benefits?: string;
  minAmount?: number;
  maxSponsors?: number;
}

const sponsorLevelConfig = {
  PLATINUM: { color: '#e5e4e2', labelKey: 'sponsors.levels.platinum' },
  GOLD: { color: '#ffd700', labelKey: 'sponsors.levels.gold' },
  SILVER: { color: '#c0c0c0', labelKey: 'sponsors.levels.silver' },
  BRONZE: { color: '#cd7f32', labelKey: 'sponsors.levels.bronze' },
  PARTNER: { color: '#52c41a', labelKey: 'sponsors.levels.partner' },
} as const;

const Sponsors: React.FC = () => {
  const { t } = useTranslation();
  // Context-tudatos megerősítő és üzenetek, hogy a panel témája elérje őket
  // (#145) — a statikus `Modal.confirm` a témán kívül rajzol.
  const { modal, message } = AntdApp.useApp();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [sponsorLevels, setSponsorLevels] = useState<SponsorLevel[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [selectedEventInForm, setSelectedEventInForm] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [summary, setSummary] = useState<SponsorSummary | null>(null);
  /** Szponzor-azonosító → hitelesített sor. A tábla második sora ebből él. */
  const [adminRows, setAdminRows] = useState<Record<string, AdminSponsorRow>>({});
  /** A NYITOTT modálisé. Külön, mert „minden esemény" nézetben lista nincs. */
  const [editingAdminRow, setEditingAdminRow] = useState<AdminSponsorRow | null>(null);
  /**
   * Melyik szponzoré a modális ÉPPEN MOST. A hitelesített olvasás aszinkron:
   * ha a szervező gyorsan zár és másikat nyit, a korábbi válasz később
   * érkezhet meg, és a MÁSIK szponzor űrlapjára írná rá az előző
   * kapcsolattartóját — pontosan az a hiba, amit a #162 zárt le, csak most a
   * hálózat felől. Ez a ref dönti el, hogy a beérkező válasz még aktuális-e.
   */
  const editingIdRef = useRef<string | null>(null);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [onlyAwaiting, setOnlyAwaiting] = useState(false);
  const [delegatesSponsor, setDelegatesSponsor] = useState<{ id: string; name: string } | null>(
    null
  );
  const [form] = Form.useForm();
  const { user } = useAuthStore();

  useEffect(() => {
    fetchEvents();
    fetchSponsors();
  }, []);

  const fetchEvents = async () => {
    try {
      const params: any = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchSponsors = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedEvent) {
        params.eventId = selectedEvent;
      } else if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }

      const response = await api.get('/sponsors', { params });
      setSponsors(response.data || []);
    } catch (error) {
      message.error(t('sponsors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSponsorLevels = async (eventId: string) => {
    try {
      const response = await api.get(`/events/${eventId}/sponsor-levels`);
      setSponsorLevels(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sponsor levels:', error);
      setSponsorLevels([]);
    }
  };

  /**
   * Az összesítő CSAK kiválasztott eseményre kérhető: a keret, a link és a
   * lemondás eseményhez kötött fogalmak, és a végpont is eseményre szűkít.
   * „Minden esemény" nézetben ezért nincs se oszlop, se link-művelet.
   */
  const fetchSummary = async (eventId?: string) => {
    if (!eventId) {
      setSummary(null);
      return;
    }
    try {
      const response = await api.get(`/events/${eventId}/sponsors/summary`);
      setSummary(response.data ?? null);
    } catch {
      setSummary(null);
    }
  };

  /**
   * A kapcsolattartók a tábla alá — CSAK kiválasztott eseményre.
   *
   * Ugyanaz a korlát, mint az összesítőnél: a hitelesített lista eseményre
   * szűkít, mert a kapu az eseményhez köti a hívót. „Minden esemény" nézetben
   * tehát nincs második sor — a szerkesztő modális viszont ott is megkapja a
   * sajátját, az egy-szponzoros végpontról.
   */
  const fetchAdminRows = async (eventId?: string) => {
    if (!eventId) {
      setAdminRows({});
      return;
    }
    try {
      const response = await api.get(`/events/${eventId}/sponsors/admin`);
      const rows = (response.data ?? []) as AdminSponsorRow[];
      setAdminRows(Object.fromEntries(rows.map((r) => [r.id, r])));
    } catch {
      setAdminRows({});
    }
  };

  useEffect(() => {
    fetchSponsors();
    fetchSummary(selectedEvent);
    fetchAdminRows(selectedEvent);
  }, [selectedEvent]);

  /**
   * A szerver állapotneve → felirat, LITERÁL kulcsokkal.
   *
   * A panel szótárkulcsai típusosak, ezért sablon-literálból nem lehet kulcsot
   * gyártani — és ez jó: egy új szerveroldali állapot így fordítási hibát ad,
   * nem néma üres cellát. Az `unknown` ág szándékosan a nyers értéket mutatja,
   * hogy a hiányt látni lehessen, ne üresnek tűnjön.
   */
  const linkStateLabel = (state: string): string => {
    switch (state) {
      case 'NOT_SENT':
        return t('sponsors.linkState.notSent');
      case 'SENT':
        return t('sponsors.linkState.sent');
      case 'OPENED':
        return t('sponsors.linkState.opened');
      case 'SUBMITTED':
        return t('sponsors.linkState.submitted');
      case 'REVOKED':
        return t('sponsors.linkState.revoked');
      case 'EXPIRED':
        return t('sponsors.linkState.expired');
      default:
        return state;
    }
  };

  const summaryOf = (id: string): SponsorSummaryRow | undefined =>
    summary?.sponsors.find((s) => s.id === id);

  /** A link-műveletek csak bekapcsolt önkiszolgálásnál léteznek. */
  /**
   * A #150 MINDEN új felülete a szponzor-önkiszolgáláshoz tartozik, tehát a
   * kapcsolójához is kötve van — nem pusztán ahhoz, hogy az összesítő
   * betöltött-e.
   *
   * MIÉRT SZÁMÍT (#150 QA). Kikapcsolt önkiszolgálásnál az oldalnak BETŰRE a
   * mai oldalnak kell lennie: hét oszlop, se szűrő, se Export, se összegző
   * sor. Minden ma élő esemény ilyen — ha itt új oszlopok jelennek meg, az
   * olyan felületet ígér a szervezőnek, ami azon az eseményen nem is működik.
   */
  const selfServiceOn = !!summary?.config.selfService;
  const linkActionsVisible = selfServiceOn;

  const refreshAll = async () => {
    await fetchSponsors();
    await fetchSummary(selectedEvent);
  };

  const handleSendLink = (record: Sponsor, resend: boolean) => {
    modal.confirm({
      title: t(resend ? 'sponsors.confirmResendLinkTitle' : 'sponsors.confirmSendLinkTitle'),
      content: t(resend ? 'sponsors.confirmResendLinkBody' : 'sponsors.confirmSendLinkBody'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      onOk: async () => {
        try {
          await api.post(`/sponsors/${record.id}/access-link`);
          message.success(t('sponsors.linkSent'));
          await refreshAll();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('sponsors.operationFailed'));
        }
      },
    });
  };

  const handleRevokeLink = (record: Sponsor) => {
    modal.confirm({
      title: t('sponsors.confirmRevokeLinkTitle'),
      content: t('sponsors.confirmRevokeLinkBody'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.delete(`/sponsors/${record.id}/access-link`);
          message.success(t('sponsors.linkRevoked'));
          await refreshAll();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('sponsors.operationFailed'));
        }
      },
    });
  };

  /**
   * Újranyitás — és NEM újraküldés. A szponzor a már kiküldött levelében lévő
   * linkkel tér vissza; ez a művelet szándékosan nem forgatja a tokent.
   */
  const handleReopen = (record: Sponsor) => {
    modal.confirm({
      title: t('sponsors.confirmReopenTitle'),
      content: t('sponsors.confirmReopenBody'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      onOk: async () => {
        try {
          await api.delete(`/sponsors/${record.id}/waive`);
          message.success(t('sponsors.reopened'));
          await refreshAll();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('sponsors.operationFailed'));
        }
      },
    });
  };

  /**
   * A linket a szervező WhatsAppon is elküldheti, ezért másolható — és a #163
   * óta a másolás VALÓBAN csak másol.
   *
   * MI VOLT ITT. Ez a gomb a `POST`-ot hívta, ami új tokent generál és levelet
   * küld. Az indoka („a nyers tokent a rendszer nem tárolja, csak a hash-ét")
   * a #151 ÓTA nem igaz: a titok-tábla őrzi a nyers tokent, mert az
   * emlékeztető-cron abból épít linket. A gomb tehát évekig azt tette, amit a
   * felirata tagadott: megölte a szponzor kezében lévő levelet, és küldött rá
   * egy újat — arra a címre, ami éppen el volt mentve.
   *
   * MIÉRT NINCS MÁR MEGERŐSÍTŐ. Nem történik semmi visszafordíthatatlan. A
   * küldés és az újraküldés megerősítője a helyén marad: azok forgatnak.
   *
   * MIÉRT NINCS `refreshAll()`. Nem változott semmi. Egy frissítés itt csak azt
   * sugallná, hogy igen.
   */
  const handleCopyLink = async (record: Sponsor) => {
    try {
      const response = await api.get(`/sponsors/${record.id}/access-link`);
      const url = response.data?.url ?? null;
      if (!url) {
        // Nincs átadható link (sosem küldték ki, visszavonták, lejárt, vagy a
        // #151 előtt ment ki, tárolt token nélkül). Az egyetlen őszinte válasz
        // az, hogy újraküldéssel lehet linkhez jutni — a régi gombbal ellentétben
        // NEM csinálunk csendben újat.
        message.warning(t('sponsors.copyLinkUnavailable'));
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
      message.success(t('sponsors.linkCopied'));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('sponsors.operationFailed'));
    }
  };

  const handleExport = async (format: 'xlsx' | 'csv') => {
    if (!selectedEvent) return;
    try {
      const response = await api.get(`/events/${selectedEvent}/sponsors/export`, {
        params: { format },
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `szponzorok.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('sponsors.operationFailed'));
    }
  };

  // Aktív esemény = isActive !== false ÉS még nincs lejárva (endDate >= ma).
  // Új szponzor csak ezekhez tehető. A header filter dropdown is ezekre szűr.
  // Szerkesztéskor a már lezárt eventet is megtartjuk a listában — különben a
  // Select érték eltűnne és form-error lenne.
  const activeEvents = events.filter(
    (e) => e.isActive !== false && (!e.endDate || dayjs(e.endDate).endOf('day').isAfter(dayjs()))
  );

  const activeEventsForForm = editingSponsor
    ? // unió: aktív + az aktuálisan szerkesztett event (akkor is ha lezárult)
      [
        ...activeEvents,
        ...events.filter(
          (e) => e.id === editingSponsor.eventId && !activeEvents.some((a) => a.id === e.id)
        ),
      ]
    : activeEvents;

  // Filter sponsors based on search text
  const filteredSponsors = sponsors
    .filter((sponsor) => {
      const searchLower = searchText.toLowerCase();
      return (
        sponsor.name.toLowerCase().includes(searchLower) ||
        sponsor.description?.toLowerCase().includes(searchLower) ||
        sponsor.Event?.title?.toLowerCase().includes(searchLower) ||
        sponsor.EventSponsorLevel?.name?.toLowerCase().includes(searchLower)
      );
    })
    // A két szűrő az ÖSSZESÍTŐBŐL dolgozik, nem a lista soraiból: a keret és a
    // fizetettség csak ott van meg. Összesítő nélkül ezért nem is szűrnek.
    .filter((sponsor) => {
      // Kikapcsolt önkiszolgálásnál a két jelölőnégyzet nem is látszik, de az
      // ÁLLAPOTUK megmarad: aki bekapcsolta egy önkiszolgáló eseményen, majd
      // átváltott egy kapcsoló nélkülire, láthatatlanul szűrt listát kapna.
      if (!selfServiceOn) return true;
      if (!(onlyIncomplete || onlyAwaiting)) return true;
      const row = summary?.sponsors.find((r) => r.id === sponsor.id);
      if (!row) return false;
      if (onlyIncomplete && !(row.allowance.remainingFree > 0 && !row.allowance.waived)) {
        return false;
      }
      if (onlyAwaiting && row.awaitingPayment === 0) return false;
      return true;
    });

  const handleCreate = () => {
    setEditingSponsor(null);
    // Egy még úton lévő hitelesített válasz ne írjon bele az ÚJ szponzor
    // űrlapjába: a ref nullázása érvényteleníti.
    editingIdRef.current = null;
    setEditingAdminRow(null);
    form.resetFields();
    setSelectedEventInForm(undefined);
    setSponsorLevels([]);
    setModalVisible(true);
  };

  const handleEdit = (record: Sponsor) => {
    setEditingSponsor(record);
    // A `resetFields()` a `setFieldsValue()` ELŐTT áll, és nem hagyható el
    // (#162). A `record` a tábla sora, az pedig a `@Public()` `GET /sponsors`-ról
    // jön, aminek a whitelistje a `contactName`-t és a `contactEmail`-t
    // szándékosan kihagyja. Ezt a két mezőt tehát a `setFieldsValue` soha nem
    // írja felül: reset nélkül az ELŐZŐ szponzornál begépelt érték bent marad az
    // űrlapon, és a mentés ráírja erre a szponzorra.
    form.resetFields();
    form.setFieldsValue({
      ...record,
      sponsorLevelId: record.sponsorLevelId || undefined,
    });
    setSelectedEventInForm(record.eventId);
    // Fetch sponsor levels for the event
    if (record.eventId) {
      fetchSponsorLevels(record.eventId);
    }
    setModalVisible(true);
    loadAdminRecord(record.id);
  };

  /**
   * A kapcsolattartó betöltése a HITELESÍTETT végpontról (#163).
   *
   * A `handleEdit` a tábla sorából tölt, az viszont a `@Public()` vetületből
   * jön, ami a két mezőt szándékosan kihagyja. A #162 a szennyeződést zárta le
   * (azóta üresen nyílnak); ez a hívás teszi őket ismét OLVASHATÓVÁ.
   *
   * A `editingIdRef` nem óvatoskodás: a válasz aszinkron, és ha közben másik
   * szponzor modálisa nyílt meg, a késői válasz ANNAK az űrlapjára írná rá ezt
   * a kapcsolattartót — ugyanaz a kár, amit a #162 javított, csak a hálózat
   * felől érkezve.
   */
  const loadAdminRecord = async (sponsorId: string) => {
    editingIdRef.current = sponsorId;
    setEditingAdminRow(null);
    try {
      const response = await api.get(`/sponsors/${sponsorId}/admin`);
      if (editingIdRef.current !== sponsorId) return;
      const row = response.data as AdminSponsorRow;
      setEditingAdminRow(row);
      form.setFieldsValue({
        contactName: row.contactName ?? undefined,
        contactEmail: row.contactEmail ?? undefined,
      });
      setAdminRows((prev) => ({ ...prev, [sponsorId]: row }));
    } catch {
      if (editingIdRef.current !== sponsorId) return;
      message.error(t('sponsors.contactLoadFailed'));
    }
  };

  const handleEventChange = (eventId: string) => {
    setSelectedEventInForm(eventId);
    if (eventId) {
      fetchSponsorLevels(eventId);
      // Clear sponsor level selection when event changes
      form.setFieldValue('sponsorLevelId', undefined);
    } else {
      setSponsorLevels([]);
    }
  };

  /**
   * A SZERVER üzenetét mutatjuk, nem általános hibát.
   *
   * A #148 óta a kiadott jeggyel rendelkező szponzor törlése 409-et ad
   * („előbb a jegyeket"). Az általános „hiba történt" mellett a szervező nem
   * tudná meg, mit kell előbb elintéznie — és azt hinné, a panel romlott el.
   */
  const handleDelete = (record: Sponsor) => {
    modal.confirm({
      title: t('common.confirm_delete'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          // `silentError`: a globális hibakezelő MINDEN elhasalt kérésre pirít
          // egyet, és ez a catch-ág is — a kiadott jegyek őrének üzenete így
          // kétszer állt a képernyőn (#150 QA). A üzenetet innentől ez a hívó
          // birtokolja, egyszer.
          await api.delete(`/sponsors/${record.id}`, { silentError: true } as never);
          message.success(t('sponsors.deleted'));
          await refreshAll();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('messages.delete_error'));
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = { ...values, socialLinks: normalizeSocialLinks(values.socialLinks) };
      if (editingSponsor) {
        // Remove eventId from values when updating
        const { eventId, ...updateData } = payload;
        await api.patch(`/sponsors/${editingSponsor.id}`, updateData);
        message.success(t('sponsors.updated'));
      } else {
        await api.post('/sponsors', payload);
        message.success(t('sponsors.created'));
      }
      setModalVisible(false);
      fetchSponsors();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('sponsors.operationFailed'));
    }
  };

  const columns: ColumnsType<Sponsor> = [
    {
      title: t('sponsors.logoColumn'),
      dataIndex: 'logo',
      key: 'logo',
      width: 120,
      render: (logo: string, record: Sponsor) => (
        // #159 — nem `Avatar`: az négyzetbe vágja (`cover`) a fekvő logókat.
        <LogoThumb
          src={logo}
          alt={record.name}
          fallback={<TrophyOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />}
        />
      ),
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      /**
       * A kapcsolattartó címe MÁSODIK SORKÉNT a név alatt, nem külön
       * oszlopként: a tábla már így is túlcsordul, és a kliensnek mind az 52
       * szponzort egy képernyőn kell átnéznie — ez az olvasás az egyetlen
       * eszköz annak eldöntésére, melyik soron áll a helyes cím.
       */
      render: (name: string, record: Sponsor) => {
        const kapcsolat = adminRows[record.id]?.contactEmail;
        return (
          <Space direction="vertical" size={0}>
            <span>{name}</span>
            {kapcsolat ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {kapcsolat}
              </Typography.Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: t('sponsors.level'),
      key: 'sponsorLevel',
      render: (_, record) => {
        // Use new sponsor level if available, otherwise fall back to legacy level
        if (record.EventSponsorLevel) {
          return (
            <Tag color={record.EventSponsorLevel.color || '#6B7280'} style={{ color: '#000' }}>
              {record.EventSponsorLevel.name}
            </Tag>
          );
        } else if (record.level) {
          const config = sponsorLevelConfig[record.level as keyof typeof sponsorLevelConfig];
          return (
            <Tag color={config.color} style={{ color: '#000' }}>
              {t(config.labelKey)}
            </Tag>
          );
        }
        return '-';
      },
      sorter: (a, b) => {
        // Sort by sponsor level priority if available
        if (a.EventSponsorLevel && b.EventSponsorLevel) {
          return a.EventSponsorLevel.level - b.EventSponsorLevel.level;
        }
        // Fall back to legacy level sorting
        const levels = ['PLATINUM', 'GOLD', 'SILVER', 'BRONZE', 'PARTNER'];
        const aIndex = a.level ? levels.indexOf(a.level) : 999;
        const bIndex = b.level ? levels.indexOf(b.level) : 999;
        return aIndex - bIndex;
      },
    },
    {
      title: t('sponsors.website'),
      dataIndex: 'website',
      key: 'website',
      render: (website: string) =>
        website ? (
          <a href={website} target="_blank" rel="noopener noreferrer">
            <GlobalOutlined /> {t('sponsors.website')}
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: t('common.event'),
      dataIndex: ['Event', 'title'],
      key: 'event',
      render: (title: string) => title || '-',
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => description || '-',
    },
    // A három szervezői oszlop CSAK kiválasztott eseménynél ÉS bekapcsolt
    // önkiszolgálásnál létezik: az összesítő eseményre szűkít, „minden
    // esemény" nézetben nincs mihez kötni, kapcsoló nélkül pedig nincs mit
    // mutatni.
    ...(selfServiceOn && summary
      ? ([
          {
            title: t('sponsors.ticketsColumn'),
            key: 'tickets',
            width: 150,
            render: (_: unknown, record: Sponsor) => {
              const row = summaryOf(record.id);
              if (!row) return '-';
              return (
                <Space direction="vertical" size={0}>
                  <span>{`${row.occupied.free} / ${row.allowance.free}`}</span>
                  {row.awaitingPayment > 0 && (
                    <Tag color="orange">
                      {t('sponsors.awaitingPaymentBadge', { count: row.awaitingPayment })}
                    </Tag>
                  )}
                  {row.allowance.overbooked && (
                    <Tag color="red">
                      {t('sponsors.overbookedNote', {
                        issued: row.occupied.free,
                        allowed: row.allowance.free,
                      })}
                    </Tag>
                  )}
                </Space>
              );
            },
          },
          {
            title: t('sponsors.linkColumn'),
            key: 'link',
            width: 170,
            render: (_: unknown, record: Sponsor) => {
              const row = summaryOf(record.id);
              if (!row) return '-';
              const label = linkStateLabel(row.link.state);
              return (
                <Space direction="vertical" size={0}>
                  <span>{label}</span>
                  {row.allowance.waived && <Tag color="default">{t('sponsors.waivedNote')}</Tag>}
                </Space>
              );
            },
          },
          {
            title: t('sponsors.lastSubmittedColumn'),
            key: 'lastSubmitted',
            width: 130,
            render: (_: unknown, record: Sponsor) => {
              const at = summaryOf(record.id)?.link.lastSubmittedAt;
              return at ? dayjs(at).format('YYYY-MM-DD') : '-';
            },
          },
        ] as ColumnsType<Sponsor>)
      : []),
    {
      title: t('common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 220,
      render: (_, record) => {
        const row = summaryOf(record.id);
        const sent = row ? row.link.state !== 'NOT_SENT' : false;
        return (
          <Space size="small">
            <Button
              type="text"
              icon={<TeamOutlined />}
              title={t('sponsors.manageDelegates')}
              onClick={() => setDelegatesSponsor({ id: record.id, name: record.name })}
            />
            {linkActionsVisible && (
              <>
                <Tooltip title={t(sent ? 'sponsors.resendLink' : 'sponsors.sendLink')}>
                  <Button
                    type="text"
                    icon={sent ? <ReloadOutlined /> : <SendOutlined />}
                    title={t(sent ? 'sponsors.resendLink' : 'sponsors.sendLink')}
                    onClick={() => handleSendLink(record, sent)}
                  />
                </Tooltip>
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  title={t('sponsors.copyLink')}
                  onClick={() => handleCopyLink(record)}
                />
                {sent && (
                  <Button
                    type="text"
                    icon={<StopOutlined />}
                    title={t('sponsors.revokeLink')}
                    onClick={() => handleRevokeLink(record)}
                  />
                )}
                {row?.allowance.waived && (
                  <Button
                    type="text"
                    icon={<UndoOutlined />}
                    title={t('sponsors.reopenSubmission')}
                    onClick={() => handleReopen(record)}
                  />
                )}
              </>
            )}
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Title level={2} style={{ margin: 0 }}>
              {t('sponsors.title')}
            </Title>
            <Badge
              count={filteredSponsors.length}
              style={{ backgroundColor: '#52c41a' }}
              showZero
              overflowCount={999}
            />
          </Space>
          <Space>
            <Input.Search
              placeholder={t('sponsors.searchPlaceholder')}
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
            />
            <Select
              style={{ width: 250 }}
              placeholder={t('sponsors.allActiveEvents')}
              allowClear
              value={selectedEvent}
              onChange={setSelectedEvent}
            >
              {activeEvents.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
            {selfServiceOn && summary && (
              <Button icon={<DownloadOutlined />} onClick={() => handleExport('xlsx')}>
                {t('sponsors.exportButton')}
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('sponsors.new_sponsor')}
            </Button>
          </Space>
        </div>

        {selfServiceOn && summary && (
          <Space size="large" wrap>
            <Typography.Text type="secondary">
              {t('sponsors.headerSummary', {
                sponsors: summary.totals.sponsors,
                entitled: summary.totals.freeEntitled,
                filled: summary.totals.freeFilled,
                paid: summary.totals.paidSeats,
                settled: summary.totals.paidSettled,
                waived: summary.totals.waivedSeats,
              })}
            </Typography.Text>
            <Checkbox
              checked={onlyIncomplete}
              onChange={(e) => setOnlyIncomplete(e.target.checked)}
            >
              {t('sponsors.filterIncomplete')}
            </Checkbox>
            <Checkbox checked={onlyAwaiting} onChange={(e) => setOnlyAwaiting(e.target.checked)}>
              {t('sponsors.filterAwaitingPayment')}
            </Checkbox>
          </Space>
        )}

        <Table
          columns={columns}
          dataSource={filteredSponsors}
          rowKey="id"
          loading={loading}
          // #161 — `scroll.x` NÉLKÜL a táblázat nem a saját dobozán belül
          // görget, hanem TÚLNYÚLIK rajta: az önkiszolgálás bekapcsolása után
          // tíz oszlop és hat sor-akció fér el itt, és a Műveletek oszlop
          // kicsúszott a kártya jobb széle fölé, rá a mögötte lévő oldalra.
          //
          // Ez egyben a `fixed: 'right'` feltétele is. Az akció-oszlop már
          // eddig is `fixed` volt, de az antd a rögzítést `scroll.x` nélkül
          // NÉMÁN figyelmen kívül hagyja — a prop ott állt, és nem csinált
          // semmit. Vagyis ez a sor nem csak a kilógást szünteti meg, hanem a
          // hat művelet elérhetőségét is helyreállítja görgetés közben.
          //
          // A `max-content` azért jó, mert a tábla szélessége a KAPCSOLÓTÓL
          // függ: kikapcsolt önkiszolgálásnál hét oszlop van, és akkor a
          // tartalom elfér — ilyenkor nem is jelenik meg görgetősáv, tehát a
          // mai kinézet változatlan marad.
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) =>
              t('sponsors.paginationTotal', { from: range[0], to: range[1], count: total }),
            showQuickJumper: true,
          }}
        />
      </Space>

      <Modal
        title={editingSponsor ? t('sponsors.edit_sponsor') : t('sponsors.new_sponsor')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        // A `destroyOnHidden` + `preserve={false}` a `resetFields()` mellé
        // tartozik (#162): bezáráskor bomoljon le az űrlap, és ne őrizze meg
        // annak a mezőnek az értékét, amit a következő megnyitás nem állít be.
        //
        // MÉRVE: a #162 hibáját egyedül a `resetFields()` javítja — e két prop
        // kivételével a spec zöld marad. Nem ezt a hibát zárják tehát, hanem a
        // következőt, és próba sincs rájuk; az okát a spec fejléce mondja el.
        destroyOnHidden
      >
        {/*
         * Amit a SZPONZOR küldött be a saját oldalán (#163).
         *
         * Csak olvasható, és a szerkesztő mezők FÖLÖTT áll, mert ez magyarázza
         * meg, amit a szervező alattuk lát: a beküldés RÁÍR a szponzor nevére
         * és kapcsolattartójára (`sponsor-page.service.ts`, `updateCompany`).
         * E nélkül a szervező nem érti, miért változott meg egy sor, amit ő
         * nem szerkesztett.
         */}
        {editingAdminRow?.lastSubmittedAt && editingAdminRow.Company ? (
          <Card size="small" style={{ marginBottom: 16 }} title={t('sponsors.submittedTitle')}>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              {t('sponsors.submittedOverwriteNote')}
            </Typography.Paragraph>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Typography.Text>
                <strong>{t('sponsors.submittedCompany')}:</strong> {editingAdminRow.Company.name}
              </Typography.Text>
              {editingAdminRow.Company.taxNumber ? (
                <Typography.Text>
                  <strong>{t('sponsors.submittedTaxNumber')}:</strong>{' '}
                  {editingAdminRow.Company.taxNumber}
                </Typography.Text>
              ) : null}
              {editingAdminRow.Company.streetAddress || editingAdminRow.Company.city ? (
                <Typography.Text>
                  <strong>{t('sponsors.submittedAddress')}:</strong>{' '}
                  {[
                    editingAdminRow.Company.postalCode,
                    editingAdminRow.Company.city,
                    editingAdminRow.Company.streetAddress,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </Typography.Text>
              ) : null}
              {editingAdminRow.Company.contactPerson ? (
                <Typography.Text>
                  <strong>{t('sponsors.submittedContact')}:</strong>{' '}
                  {editingAdminRow.Company.contactPerson}
                </Typography.Text>
              ) : null}
              {editingAdminRow.Company.contactPhone ? (
                <Typography.Text>
                  <strong>{t('sponsors.submittedPhone')}:</strong>{' '}
                  {editingAdminRow.Company.contactPhone}
                </Typography.Text>
              ) : null}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('sponsors.submittedAt')}:{' '}
                {dayjs(editingAdminRow.lastSubmittedAt).format('YYYY. MM. DD. HH:mm')}
              </Typography.Text>
            </Space>
          </Card>
        ) : null}

        <Form form={form} layout="vertical" onFinish={handleSubmit} preserve={false}>
          <Form.Item
            name="eventId"
            label={t('common.event')}
            rules={[{ required: true, message: t('common.selectEventRequired') }]}
            extra={
              !editingSponsor && activeEvents.length === 0
                ? t('sponsors.noActiveEvents')
                : undefined
            }
          >
            <Select
              placeholder={t('sponsors.selectActiveEvent')}
              onChange={handleEventChange}
              disabled={!editingSponsor && activeEvents.length === 0}
            >
              {activeEventsForForm.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label={t('common.name')}
            rules={[{ required: true, message: t('common.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="sponsorLevelId"
            label={t('sponsors.sponsorLevel')}
            rules={[{ required: true, message: t('sponsors.levelRequired') }]}
          >
            <Select
              placeholder={
                sponsorLevels.length === 0
                  ? t('sponsors.selectEventFirst')
                  : t('sponsors.selectLevel')
              }
              disabled={sponsorLevels.length === 0}
            >
              {sponsorLevels
                .sort((a, b) => a.level - b.level)
                .map((level) => (
                  <Option key={level.id} value={level.id}>
                    <Space>
                      {level.color && (
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: level.color,
                            borderRadius: 2,
                            border: '1px solid #d9d9d9',
                          }}
                        />
                      )}
                      {level.name}
                      {level.minAmount && (
                        <span style={{ color: '#888', fontSize: '12px' }}>
                          (€{level.minAmount.toFixed(2)})
                        </span>
                      )}
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="contactName"
            label={t('sponsors.contactName')}
            extra={t('sponsors.contactHint')}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="contactEmail"
            label={t('sponsors.contactEmail')}
            rules={[{ type: 'email', message: t('sponsors.contactEmailInvalid') }]}
          >
            <Input type="email" />
          </Form.Item>

          <Form.Item name="logo" label={t('sponsors.logo')}>
            {/* A ma élő szponzorlogók külső oldalra hivatkoznak, ezért a
                feltöltés mellett a beillesztés is megmarad. */}
            <ImageUpload uploadUrl="/api/upload/logo" fieldName="logo" maxSize={2} allowUrlInput />
          </Form.Item>

          <Form.Item name="website" label={t('sponsors.website')}>
            <Input type="url" placeholder={t('sponsors.websitePlaceholder')} />
          </Form.Item>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea rows={4} placeholder={t('sponsors.descriptionPlaceholder')} />
          </Form.Item>

          <Form.Item name="longDescription" label={t('sponsors.longDescription')}>
            <TextArea rows={5} placeholder={t('sponsors.longDescriptionPlaceholder')} />
          </Form.Item>

          <Form.Item label={t('sponsors.socialLinks')} extra={t('sponsors.socialLinksExtra')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {SOCIAL_FIELDS.map(({ key, label, placeholder }) => (
                <Form.Item key={key} name={['socialLinks', key]} noStyle>
                  <Input type="url" addonBefore={label} placeholder={placeholder} />
                </Form.Item>
              ))}
            </Space>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingSponsor ? t('common.update') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('sponsors.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <SponsorDelegates
        sponsorId={delegatesSponsor?.id ?? null}
        sponsorName={delegatesSponsor?.name}
        visible={!!delegatesSponsor}
        onClose={() => setDelegatesSponsor(null)}
      />
    </Card>
  );
};

export default Sponsors;
